import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal';
import { Input } from '../ui/Input';
import { VBButton } from '../ui/VBButton';
import * as serverOrgs from '../../../api/server/orgs';
import { useToast } from '../../components/ui/Toast';
import { logger } from '../../../utils/logger';

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: serverOrgs.Organization | null; // If provided, edit mode
  onSaved?: (org: serverOrgs.Organization | serverOrgs.OrganizationWithUsers) => void;
}

export const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  onClose,
  organization,
  onSaved
}) => {
  const [name, setName] = useState('');
  const [image, setImage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const { addToast } = useToast();

  const isEditMode = !!organization;

  // Update form when organization changes
  useEffect(() => {
    if (isOpen) {
      if (organization) {
        setName(organization.name || '');
        setImage(organization.image || '');
      } else {
        setName('');
        setImage('');
      }
      setError('');
    }
  }, [isOpen, organization]);

  // Validate logo URL scheme (https only)
  const imageUrl = image.trim();
  const isImageUrlValid = !imageUrl || imageUrl.startsWith('https://');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Organization name is required');
      return;
    }

    if (!isImageUrlValid) {
      setError('Logo URL must use https://');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if (isEditMode && organization) {
        // Update existing organization
        const updated = await serverOrgs.updateOrg(organization.org_id, {
          name: name.trim(),
          image: image.trim() || null
        });
        logger.info('[OrganizationModal] Organization updated successfully');
        addToast({ type: 'success', title: 'Organization updated' });
        onSaved?.(updated);
        onClose();
      } else {
        // Create new organization
        const created = await serverOrgs.createOrg({
          name: name.trim(),
          image: image.trim() || undefined
        });
        logger.info('[OrganizationModal] Organization created successfully');
        addToast({
          type: 'success',
          title: 'Organization created',
          message: 'You can manage it in Org Admin settings.'
        });
        onSaved?.(created);
        onClose();
      }
    } catch (err) {
      logger.error('[OrganizationModal] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
      setError('');
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm" closeOnOverlayClick={!isSaving}>
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <ModalHeader onClose={handleClose}>
          {isEditMode ? 'Edit Organization' : 'Create Organization'}
        </ModalHeader>

        <ModalBody>
          {error && (
            <div className="mb-4 p-3 bg-vb-danger/10 border border-vb-danger/30 rounded-lg text-vb-danger text-sm dark:bg-vb-danger/20 dark:border-vb-danger/40">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Input
              type="text"
              label="Organization Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter organization name"
              maxLength={100}
              required
              disabled={isSaving}
              autoFocus
            />

            <Input
              type="url"
              label="Logo URL (Optional)"
              value={image}
              onChange={(e) => setImage(e.target.value)}
              placeholder="https://example.com/logo.png"
              disabled={isSaving}
              error={imageUrl && !isImageUrlValid ? 'Logo URL must use https://' : undefined}
            />

            {/* Logo preview */}
            {imageUrl && isImageUrlValid && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
                  Preview:
                </span>
                <img
                  src={imageUrl}
                  alt="Logo preview"
                  className="w-10 h-10 rounded-lg object-cover border border-vb-border dark:border-vb-border-dark"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <VBButton variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </VBButton>
          <VBButton
            variant="primary"
            disabled={isSaving || !name.trim() || !isImageUrlValid}
            onClick={(e) => {
              const form = (e.target as HTMLElement).closest('form');
              if (form) form.requestSubmit();
            }}
          >
            {isSaving
              ? isEditMode
                ? 'Updating...'
                : 'Creating...'
              : isEditMode
                ? 'Update'
                : 'Create'}
          </VBButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};
